import { Button } from '@avanta/shared/components/ui/Button';
import { Calendar } from '@avanta/shared/components/ui/Calendar';
import { Card } from '@avanta/shared/components/ui/Card';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { cn } from '@avanta/shared/utils/cn';
import { ChevronRight, GraduationCap } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { ROUTES } from 'constants/routes';
import { WellbeingTypes } from 'constants/wellbeing';
import { useWellbeingContent } from 'hooks/useWellbeingContent';
import { formatDateISO } from 'utils/date';
import { TrainingCalendarNavButtons } from './components/TrainingCalendarNavButtons';
import { TrainingCard } from './components/TrainingCard';
import {
  filterTrainingsByCurrentYear,
  firstTrainingDateInMonth,
  nextTrainingDate,
  previousTrainingDate,
} from './utils';

const JANUARY = new Date(new Date().getFullYear(), 0, 1);
const DECEMBER = new Date(new Date().getFullYear(), 11, 31);
const CURRENT_YEAR = new Date().getFullYear();

export interface TrainingCalendarProps {
  hideViewAllTrainings?: boolean;
}

export const TrainingCalendar = ({
  hideViewAllTrainings = false,
}: TrainingCalendarProps): React.ReactElement | null => {
  const navigate = useNavigate();
  const { t } = useTranslation(LOCIZE_NS.WELL_BEING);
  const { data: trainingsList, isLoading } = useWellbeingContent(WellbeingTypes.trainings);
  const trainingsByCurrentYear = useMemo(
    () => filterTrainingsByCurrentYear(trainingsList || []),
    [trainingsList]
  );
  const firstTrainingDate = firstTrainingDateInMonth(new Date(), trainingsByCurrentYear);
  const [day, setDay] = useState<Date | null | undefined>(null);
  const [calendarMonth, setCalendarMonth] = useState<Date>(new Date());
  const selectedDate = day === null ? firstTrainingDate : day;
  const training = selectedDate ? trainingsByCurrentYear[formatDateISO(selectedDate)] : undefined;
  const disablePrevious =
    !selectedDate || !previousTrainingDate(selectedDate, trainingsByCurrentYear);
  const disableNext = !selectedDate || !nextTrainingDate(selectedDate, trainingsByCurrentYear);
  const markedDates = trainingsByCurrentYear
    ? Object.keys(trainingsByCurrentYear).map(date => new Date(date))
    : [];

  const handleDateSelect = (date: Date | undefined): void => {
    if (!date) return;
    setDay(date);
  };

  const handleMonthChange = (nextMonthView: Date): void => {
    setCalendarMonth(nextMonthView);
    setDay(firstTrainingDateInMonth(nextMonthView, trainingsByCurrentYear) ?? undefined);
  };

  const handleViewAllTrainings = (): void => {
    navigate(`${ROUTES.WELL_BEING}/trainings`);
  };

  const handlePreviousClick = (): void => {
    if (!selectedDate) return;
    const previous = previousTrainingDate(selectedDate, trainingsByCurrentYear);
    if (!previous) return;
    setDay(previous);
    setCalendarMonth(previous);
  };

  const handleNextClick = (): void => {
    if (!selectedDate) return;
    const next = nextTrainingDate(selectedDate, trainingsByCurrentYear);
    if (!next) return;
    setDay(next);
    setCalendarMonth(next);
  };

  if (isLoading) return null;

  return (
    <div className="flex w-full flex-col">
      <Card
        title={`${t('Trainings.heading')} ${CURRENT_YEAR}`}
        icon={GraduationCap}
        className="p-0 px-4 pt-2 pb-6 am-3xl:p-6"
      >
        <div className="flex flex-col gap-6 am-3xl:flex-row am-3xl:items-start am-3xl:gap-10">
          <div className="flex w-full am-3xl:block am-3xl:max-w-72">
            <Calendar
              selected={selectedDate}
              month={calendarMonth}
              onMonthChange={handleMonthChange}
              onSelect={handleDateSelect}
              startMonth={JANUARY}
              endMonth={DECEMBER}
              className="w-full bg-transparent p-0"
              showOutsideDays
              modifiers={{
                marked: markedDates,
              }}
            />
          </div>
          <div className="flex w-full flex-1 flex-col gap-6">
            <div className="hidden w-full items-center justify-end am-3xl:flex">
              <TrainingCalendarNavButtons
                disablePrevious={disablePrevious}
                disableNext={disableNext}
                onPreviousClick={handlePreviousClick}
                onNextClick={handleNextClick}
              />
            </div>
            {training ? (
              <TrainingCard training={training} />
            ) : (
              <div className="flex min-h-48 items-center justify-center rounded-xl border border-gray-200 bg-white text-center text-sm text-gray-500">
                {t('Trainings.empty')}
              </div>
            )}
            {!hideViewAllTrainings ? (
              <Button
                variant="link"
                className="hidden h-fit shrink-0 self-end p-0 text-teal-600 hover:text-teal-500 am-3xl:flex"
                onClick={handleViewAllTrainings}
              >
                {t('TrainingCalendar.viewAllTrainings.cta', {
                  defaultValue: t('TrainingCalendar.viewAllTrainings'),
                })}
                <ChevronRight className="size-4" />
              </Button>
            ) : null}
          </div>
        </div>
      </Card>
      <div
        className={cn(
          'mt-4 flex items-center am-3xl:hidden',
          hideViewAllTrainings ? 'justify-end' : 'justify-between'
        )}
      >
        {!hideViewAllTrainings ? (
          <Button
            variant="link"
            className="h-fit shrink-0 p-0 text-teal-600 hover:text-teal-500"
            onClick={handleViewAllTrainings}
          >
            {t('TrainingCalendar.viewAllTrainings.cta', {
              defaultValue: t('TrainingCalendar.viewAllTrainings'),
            })}
            <ChevronRight className="size-4" />
          </Button>
        ) : null}
        <TrainingCalendarNavButtons
          disablePrevious={disablePrevious}
          disableNext={disableNext}
          onPreviousClick={handlePreviousClick}
          onNextClick={handleNextClick}
        />
      </div>
    </div>
  );
};
